import { makeStyles } from '@material-ui/core/styles'
import { interactiveCardHoverStyles } from 'lib/styles/interactive-card-hover'
import { getCoachAvatarFontSize } from 'components/coach-user-avatar'
import {
  CALLOUT_BACKGROUND,
  getOutlinedSelectMenuPaperStyles,
} from 'lib/form-field-styles'

const BORDER = '#E0E0E0'
const INK = '#172024'

// Tokens for the public golfer pages (checkout, unavailable, confirmation).
// They match the redesigned group event registration
// (`things-to-do/events/register/.../redesign/styles.js`) and pga.com: serif
// headings, 24px cards with a soft border and shadow, muted secondary text,
// and the theme's own primary button.
export const PAGE_BACKGROUND = '#F5F6F8'
export const CARD_BORDER = '#E6E8EC'
const CARD_SHADOW =
  '0 1px 2px rgba(0, 10, 22, 0.04), 0 8px 24px rgba(0, 10, 22, 0.04)'
export const MUTED_TEXT = '#5F6B76'
export const NAVY_TINT = 'rgba(0, 35, 75, 0.08)'
export const SERIF = "'Source Serif Pro', serif"
// offeringsRefresh primary button: `primary.lightest` and `primary.main`.
const ACTION_BLUE = '#3268A7'
const ACTION_BLUE_HOVER = '#00234B'

// Applied under `page` and `narrowPage` only, so the club pages (inside the
// member shell) keep their own look.
const publicPageStyles = theme => ({
  '& $card': {
    borderColor: CARD_BORDER,
    borderRadius: 24,
    boxShadow: CARD_SHADOW,
    [theme.breakpoints.down('xs')]: { borderRadius: 20 },
  },
  '& $cardHeader': {
    padding: theme.spacing(4, 4, 0),
    borderBottom: 0,
    [theme.breakpoints.down('xs')]: { padding: theme.spacing(2.5, 2.5, 0) },
  },
  '& $cardBody': {
    padding: theme.spacing(2, 4, 4),
    [theme.breakpoints.down('xs')]: { padding: theme.spacing(2, 2.5, 2.5) },
  },
  '& $cardTitleText': {
    fontFamily: SERIF,
    fontSize: 22,
    lineHeight: 1.25,
    color: theme.palette.primary.main,
  },
  '& $cardSubtitle': {
    marginTop: theme.spacing(0.75),
    color: MUTED_TEXT,
    lineHeight: 1.5,
  },
  '& $sectionLabel': {
    fontSize: 16,
    color: theme.palette.text.primary,
  },
  '& $offeringTitle': {
    fontFamily: SERIF,
    fontSize: 20,
    color: theme.palette.primary.main,
  },
  '& $iconTile': {
    borderRadius: '50%',
    backgroundColor: NAVY_TINT,
  },
  '& $metaRow': { color: MUTED_TEXT },
  '& $muted': { color: MUTED_TEXT },
  '& $markdown': { color: theme.palette.text.primary },
  '& $divider': { backgroundColor: CARD_BORDER },
  '& $stepNumber': { backgroundColor: PAGE_BACKGROUND, color: MUTED_TEXT },
  '& $stepNumberActive': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  '& $stepNumberDone': { backgroundColor: theme.palette.secondary.light },
  '& $stepTitle': { color: theme.palette.text.primary },
  '& $stepCaption': { color: MUTED_TEXT },
  '& $totalRow': {
    color: theme.palette.text.primary,
    '& span:last-child': {
      fontFamily: SERIF,
      fontSize: 24,
      color: theme.palette.primary.main,
    },
  },
  '& $detailRow + $detailRow': { borderTopColor: CARD_BORDER },
  '& $noticeCard': {
    borderColor: CARD_BORDER,
    borderRadius: 16,
    backgroundColor: PAGE_BACKGROUND,
  },
  '& $noticeIcon': { backgroundColor: NAVY_TINT },
  '& $confirmIcon': {
    width: 48,
    height: 48,
    backgroundColor: NAVY_TINT,
    color: theme.palette.primary.main,
    '& svg': { fontSize: 24 },
  },
  '& $secureNote, & $helpLine': { color: MUTED_TEXT },
  // The shared upload form's contained primary buttons (pick videos, Share
  // Videos) keep the muted blue here. The attribute selector matches the
  // button whether or not the theme suffixes its class names.
  '& $uploadForm [class*="MuiButton-containedPrimary"]:not(.Mui-disabled)': {
    backgroundColor: ACTION_BLUE,
    color: theme.palette.common.white,
    '&:hover': { backgroundColor: ACTION_BLUE_HOVER },
  },
})

// Shared by every golfer-side Virtual Coaching screen. Colors come from the
// theme (the checkout and confirmation use `themes.offeringsRefresh`, the club
// pages use the PGA Coach theme) or the shared lib/styles tokens.
export const useStyles = makeStyles(theme => ({
  // Card header: title and optional subtitle on the left, an optional action
  // on the right, and a divider below. The public and member pages override it.
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(2.5, 3),
    borderBottom: `1px solid ${BORDER}`,
  },
  cardTitleBlock: { minWidth: 0 },
  cardTitle: {
    color: INK,
    marginBottom: theme.spacing(0.25),
  },
  cardSubtitle: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    lineHeight: 1.4,
  },
  page: {
    boxSizing: 'border-box',
    width: '100%',
    maxWidth: 1120,
    margin: '0 auto',
    padding: theme.spacing(0, 3, 8),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(0, 2, 6),
    },
    ...publicPageStyles(theme),
  },
  narrowPage: {
    boxSizing: 'border-box',
    width: '100%',
    maxWidth: 720,
    margin: '0 auto',
    padding: theme.spacing(0, 3, 8),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(0, 2, 6),
    },
    ...publicPageStyles(theme),
  },
  // Detail and upload pages: the page header and the content share one centered
  // column inside the main content area (full width on mobile), the same 832px
  // as the account detail pages.
  memberLayout: {
    width: '100%',
    maxWidth: 832,
    margin: '0 auto',
  },
  clubPage: {
    boxSizing: 'border-box',
    maxWidth: 960,
    padding: theme.spacing(3, 4.5, 6),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(3, 2, 6),
    },
  },
  srOnly: {
    position: 'absolute',
    width: 1,
    height: 1,
    margin: -1,
    padding: 0,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
  card: {
    border: `1px solid ${BORDER}`,
    borderRadius: 12,
    backgroundColor: theme.palette.common.white,
    overflow: 'hidden',
    marginBottom: theme.spacing(3),
  },
  cardBody: {
    padding: theme.spacing(3),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2),
    },
  },
  cardTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  cardTitleText: {
    fontFamily: theme.typography.fontFamily,
    fontSize: '1.25rem',
    fontWeight: 700,
    lineHeight: 1.3,
    color: INK,
  },
  sectionLabel: {
    fontFamily: theme.typography.fontFamily,
    fontSize: '0.875rem',
    fontWeight: 700,
    color: INK,
    marginBottom: theme.spacing(1.5),
  },
  divider: {
    margin: theme.spacing(3, 0),
  },
  muted: {
    color: theme.palette.text.secondary,
  },
  // Checkout
  checkoutGrid: {
    marginTop: theme.spacing(1),
  },
  offeringRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
  },
  iconTile: {
    flexShrink: 0,
    width: 48,
    height: 48,
    borderRadius: 12,
    backgroundColor: CALLOUT_BACKGROUND,
    color: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  offeringTitle: {
    fontFamily: theme.typography.fontFamily,
    fontSize: '1.0625rem',
    fontWeight: 700,
    lineHeight: 1.3,
    color: INK,
  },
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    '& svg': {
      fontSize: 18,
    },
  },
  coachAvatar: {
    width: 24,
    height: 24,
    fontSize: '0.75rem',
  },
  // "Your coach" panel under the offering on the checkout. Rounded-square
  // photo like the coach profile on pga.com; the theme gives every Avatar a
  // 110px circle, so size and shape are set here.
  coachCard: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(2.5),
    padding: theme.spacing(1.5, 2),
    border: `1px solid ${CARD_BORDER}`,
    borderRadius: 16,
  },
  coachCardAvatar: {
    flexShrink: 0,
    width: 48,
    height: 48,
    borderRadius: 12,
    fontSize: '1rem',
    fontWeight: 700,
    backgroundColor: NAVY_TINT,
    color: theme.palette.primary.main,
  },
  coachCardName: {
    margin: 0,
    color: theme.palette.primary.main,
    fontFamily: SERIF,
    fontSize: 18,
    fontWeight: 700,
    lineHeight: 1.3,
  },
  coachCardLabel: {
    margin: 0,
    color: MUTED_TEXT,
    fontSize: 13,
    lineHeight: 1.4,
  },
  markdown: {
    fontSize: '0.9375rem',
    lineHeight: 1.6,
    color: INK,
    overflowWrap: 'anywhere',
    '& p': { margin: theme.spacing(0, 0, 1.5) },
    '& ul, & ol': {
      margin: theme.spacing(0, 0, 1.5),
      paddingLeft: theme.spacing(2.5),
    },
    '& > :last-child': { marginBottom: 0 },
  },
  steps: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'grid',
    gap: theme.spacing(2),
  },
  step: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
  },
  stepNumber: {
    flexShrink: 0,
    width: 28,
    height: 28,
    borderRadius: '50%',
    backgroundColor: CALLOUT_BACKGROUND,
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  stepNumberActive: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  stepNumberDone: {
    backgroundColor: theme.palette.success?.main || '#2e7d32',
    color: '#fff',
  },
  stepTitle: {
    fontSize: '0.9375rem',
    fontWeight: 700,
    color: INK,
    // Same height as the number circle, so the first line centers on it.
    lineHeight: '28px',
  },
  stepCaption: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.45,
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    fontWeight: 700,
    fontSize: '1.125rem',
    color: INK,
  },
  backLink: {
    height: 'auto',
    margin: theme.spacing(3, 0, 2),
    padding: 0,
    color: MUTED_TEXT,
    fontSize: 14,
    fontWeight: 600,
    textDecoration: 'none',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: 'transparent',
      color: theme.palette.primary.main,
      textDecoration: 'none',
    },
  },
  secureNote: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(1.5),
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
    '& svg': { fontSize: 16 },
  },
  helpLine: {
    marginTop: theme.spacing(2),
    textAlign: 'center',
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
  },
  // The line under "Pay": why it is disabled, or why the last click did not go
  // through (the Stripe message is also a toast that is easy to miss).
  payHint: {
    marginTop: theme.spacing(1.5),
    textAlign: 'center',
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
  },
  payHintError: { color: theme.palette.error.dark },
  // The sign-in prompt is a shared TipWell. `&&` outranks its own sheet so it
  // takes the same soft panel look as the rest of the public pages.
  signInTip: {
    '&& > *': {
      borderColor: CARD_BORDER,
      borderRadius: 16,
      backgroundColor: PAGE_BACKGROUND,
    },
  },
  // Marketing opt-in card; same look as the group event registration redesign
  // (#4160) on this page's soft panel tokens and 16px radius.
  optIn: {
    display: 'flex',
    cursor: 'pointer',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    border: `1px solid ${CARD_BORDER}`,
    borderRadius: 16,
    backgroundColor: PAGE_BACKGROUND,
    [theme.breakpoints.down('xs')]: {
      gap: theme.spacing(1.5),
      padding: theme.spacing(2.5),
    },
  },
  optInCheckbox: {
    padding: theme.spacing(0.5),
    color: theme.palette.primary.main,
    '&.Mui-checked': { color: theme.palette.primary.main },
    // 44px touch target; negative margins keep the 24px icon on the card's
    // padding edge so the target adds no extra height or width.
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1.25),
      margin: theme.spacing(-1.25),
    },
  },
  optInHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.down('xs')]: {
      gap: theme.spacing(1.5),
      marginBottom: theme.spacing(2),
    },
  },
  optInLogo: { flexShrink: 0 },
  optInTitle: {
    margin: 0,
    fontSize: 16,
    lineHeight: 1.3,
  },
  optInBody: {
    margin: 0,
    color: MUTED_TEXT,
    fontSize: 14,
    lineHeight: 1.5,
  },
  optInConsent: {
    margin: theme.spacing(1, 0, 0),
    color: MUTED_TEXT,
    fontSize: 12,
    lineHeight: 1.5,
    [theme.breakpoints.down('xs')]: { marginTop: theme.spacing(1.5) },
  },
  tipTitle: {
    display: 'block',
    marginBottom: theme.spacing(0.5),
    fontSize: 16,
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  // Same text as the markdown paragraphs in the order summary.
  tipBody: {
    display: 'block',
    fontSize: '0.9375rem',
    lineHeight: 1.6,
    color: theme.palette.text.primary,
  },
  tipActions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
  },
  learnMore: {
    color: theme.palette.primary.main,
    fontWeight: 700,
    cursor: 'pointer',
    background: 'none',
    border: 0,
    padding: 0,
    font: 'inherit',
    textDecoration: 'underline',
  },
  // Purchase confirmed
  confirmHero: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    margin: theme.spacing(6, 0, 4),
  },
  unavailableCard: {
    boxSizing: 'border-box',
    maxWidth: 480,
    margin: theme.spacing(4, 'auto', 0),
    padding: theme.spacing(5, 4),
    border: `1px solid ${CARD_BORDER}`,
    borderRadius: 24,
    boxShadow: CARD_SHADOW,
    backgroundColor: theme.palette.common.white,
    textAlign: 'center',
    '& $confirmIcon': { margin: theme.spacing(0, 'auto', 2.5) },
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(4, 2.5),
      borderRadius: 20,
    },
  },
  unavailableTitle: {
    marginBottom: theme.spacing(1.5),
    fontFamily: SERIF,
    fontSize: 24,
    lineHeight: 1.25,
    color: theme.palette.primary.main,
  },
  unavailableAction: {
    marginTop: theme.spacing(3),
  },
  confirmTitle: {},
  confirmIcon: {
    width: 72,
    height: 72,
    borderRadius: '50%',
    backgroundColor: '#DDF5E1',
    color: '#1B7F37',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
    '& svg': { fontSize: 40 },
  },
  detailRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 0),
    '& + &': { borderTop: `1px solid ${BORDER}` },
  },
  noticeCard: {
    display: 'flex',
    gap: theme.spacing(2),
    padding: theme.spacing(2.5),
    marginBottom: theme.spacing(3),
    borderRadius: 12,
    border: `1px solid ${BORDER}`,
    backgroundColor: CALLOUT_BACKGROUND,
  },
  noticeIcon: {
    flexShrink: 0,
    width: 40,
    height: 40,
    borderRadius: '50%',
    backgroundColor: '#D9E0E4',
    color: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  centerAction: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(4),
    textAlign: 'center',
  },
  // Marker for the shared upload form. Under the public pages (`publicPageStyles`)
  // its contained primary buttons keep the confirmation page's muted blue; in
  // the member shell they use the theme's own contained button.
  uploadForm: {},
  // The success hero: the navy tint circle at 64px. Separate from
  // `confirmIcon` so the size does not change the unavailable card.
  confirmIconSuccess: {},
  // The failed hero: same circle, red tint (see `confirmPage`).
  confirmIconError: {},
  // Purchase confirmed page only: a narrower column with compact, smaller
  // radius cards (the reference success page), on the same public tokens.
  // Defined after `narrowPage` so these rules win at equal specificity.
  confirmPage: {
    maxWidth: 678,
    // Padding (not the hero's margin) so the space above the icon does not
    // collapse through the page.
    paddingTop: theme.spacing(5),
    [theme.breakpoints.down('xs')]: { paddingTop: theme.spacing(3) },
    '& $confirmHero': { margin: theme.spacing(0, 0, 3) },
    '& $confirmIconSuccess': {
      // Colors (navy tint circle, navy check) come from `confirmIcon`.
      width: 64,
      height: 64,
      marginBottom: theme.spacing(1.5),
      '& svg': { fontSize: 32 },
    },
    '& $confirmIconError': {
      backgroundColor: '#FDE8E8',
      color: theme.palette.error.main,
    },
    '& $confirmTitle': {
      fontFamily: SERIF,
      fontWeight: 700,
      fontSize: 32,
      lineHeight: 1.2,
      color: theme.palette.primary.main,
      [theme.breakpoints.down('xs')]: { fontSize: 26 },
    },
    '& $confirmSubtitle': { marginTop: theme.spacing(1), lineHeight: 1.5 },
    '& $card': {
      borderRadius: 10,
      boxShadow: '0 1px 2px rgba(0, 10, 22, 0.05)',
      marginBottom: theme.spacing(2.5),
    },
    '& $cardHeader': { padding: theme.spacing(2.75, 2.75, 0) },
    '& $cardBody': { padding: theme.spacing(1.5, 2.75, 2.75) },
    '& $cardTitleText': { fontSize: 22, fontWeight: 700 },
    '& $cardSubtitle': { marginTop: theme.spacing(0.25), fontSize: 14 },
    '& $detailRow': { padding: theme.spacing(1.5, 0) },
    '& $noticeCard': {
      alignItems: 'flex-start',
      gap: theme.spacing(1.75),
      padding: theme.spacing(2.25, 2.5),
      marginBottom: theme.spacing(2.5),
      borderRadius: 10,
      borderColor: '#D5DAE0',
      backgroundColor: '#EEF0F3',
    },
    '& $noticeIcon': {
      width: 38,
      height: 38,
      backgroundColor: '#DFE3E8',
      '& svg': { fontSize: 20 },
    },
    '& $stepNumber': { backgroundColor: '#EEF0F3' },
    '& $stepNumberActive': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    },
    '& $centerAction': {
      gap: theme.spacing(1.25),
      marginTop: theme.spacing(3),
    },
  },
  confirmSubtitle: {},
  orderItemName: { fontWeight: 600 },
  orderItemPrice: { fontWeight: 700 },
  // Upload
  dropZone: {
    border: `2px dashed ${BORDER}`,
    borderRadius: 12,
    padding: theme.spacing(3),
    textAlign: 'center',
    backgroundColor: '#FAFBFC',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  fileList: {
    listStyle: 'none',
    margin: theme.spacing(2, 0, 0),
    padding: 0,
    display: 'grid',
    gap: theme.spacing(1.5),
  },
  fileItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    border: `1px solid ${BORDER}`,
    borderRadius: 12,
    [theme.breakpoints.down('xs')]: {
      flexWrap: 'wrap',
    },
  },
  fileMeta: {
    flex: 1,
    minWidth: 0,
  },
  fileName: {
    fontSize: '0.9375rem',
    fontWeight: 600,
    color: INK,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  fileStatus: {
    fontSize: '0.8125rem',
    color: theme.palette.text.secondary,
  },
  fileStatusError: {
    color: theme.palette.error.main,
  },
  fileActions: {
    display: 'flex',
    gap: theme.spacing(0.5),
    flexShrink: 0,
  },
  progress: {
    marginTop: theme.spacing(0.75),
    height: 6,
    borderRadius: 3,
  },
  formField: {
    marginBottom: theme.spacing(3),
  },
  levelOption: {
    alignItems: 'flex-start',
    margin: 0,
  },
  // Golf level menu: the shared outlined select menu (same as the coach
  // availability and calendar location selects), with a muted second line.
  // The `li` rules repeat the helper's menu item rules for the offeringsRefresh
  // theme, whose nested style sheets suffix `.MuiMenuItem-root`.
  selectMenuPaper: {
    ...getOutlinedSelectMenuPaperStyles(theme),
    // The helper clips with `overflow: hidden`, which beats Popover's
    // `overflowY: auto`. These options wrap, so the menu must scroll.
    overflowY: 'auto',
    '& .MuiMenuItem-root, & li': {
      ...getOutlinedSelectMenuPaperStyles(theme)['& .MuiMenuItem-root'],
      alignItems: 'flex-start',
      whiteSpace: 'normal',
    },
    '& li.Mui-selected, & li.Mui-selected:hover': {
      backgroundColor: CALLOUT_BACKGROUND,
    },
  },
  selectOptionSupport: {
    display: 'block',
    marginTop: 2,
    fontSize: 12,
    fontWeight: 400,
    lineHeight: 1.4,
    color: MUTED_TEXT,
  },
  selectPlaceholder: { color: MUTED_TEXT },
  // Request detail and upload pages. They stay in the member shell, so they
  // take the /club member styles (the Lesson Packs and My Coach cards), not the
  // public page tokens: 12px radius, 1px divider border, no shadow, sans-serif
  // 18px/700 section titles with no header divider, and the theme's own
  // buttons. Defined after `clubPage` so these rules win at equal specificity.
  memberPage: {
    width: '100%',
    '& $card': {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 12,
      boxShadow: 'none',
      backgroundColor: theme.palette.common.white,
      marginBottom: theme.spacing(2),
    },
    '& $cardHeader': {
      padding: theme.spacing(2.5, 2, 0),
      borderBottom: 0,
    },
    '& $cardBody': { padding: theme.spacing(1.5, 2, 2.5) },
    '& $cardTitleText': {
      margin: 0,
      fontSize: '1.125rem',
      fontWeight: 700,
      lineHeight: 1.3,
      color: theme.palette.text.primary,
    },
    '& $cardSubtitle': {
      marginTop: theme.spacing(0.5),
      color: theme.palette.text.secondary,
      fontSize: '0.8125rem',
    },
    '& $muted': { color: theme.palette.text.secondary },
    // Steps: the /club stepper tokens (the booking type section nav and the
    // signup stepper): an outlined grey circle for upcoming steps, a light blue
    // circle with a blue ring for the current one, and a filled blue circle
    // with a white check once done. The public pages keep their own.
    '& $stepNumber': {
      boxSizing: 'border-box',
      border: '1px solid #587C8C',
      backgroundColor: theme.palette.common.white,
      color: '#587C8C',
      fontWeight: 600,
    },
    '& $stepNumberActive': {
      borderColor: theme.palette.blue?.main,
      backgroundColor: '#E8EFFF',
      color: theme.palette.blue?.main,
    },
    '& $stepNumberDone': {
      borderColor: theme.palette.blue?.main,
      backgroundColor: theme.palette.blue?.main,
      color: theme.palette.common.white,
    },
    '& $stepTitle': { fontWeight: 600, color: '#172024' },
    '& $stepCaption': { color: theme.palette.text.secondary },
    [theme.breakpoints.down('xs')]: {
      '& $cardHeader': { padding: theme.spacing(2, 2, 0) },
    },
  },
  // Coach identity at the top of the request page: the My Coach card's avatar
  // (40px circle, navy on light blue) and bold navy name, with no panel.
  memberCoach: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  memberCoachAvatar: {
    flexShrink: 0,
    width: 40,
    height: 40,
    fontSize: getCoachAvatarFontSize(40),
    fontWeight: 500,
    color: '#00205B',
    backgroundColor: '#E8F1FF',
    textTransform: 'uppercase',
  },
  memberCoachName: {
    margin: 0,
    fontSize: '1.125rem',
    fontWeight: 700,
    lineHeight: 1.3,
    color: '#00205B',
  },
  memberCoachLabel: {
    margin: 0,
    fontSize: '0.8125rem',
    lineHeight: 1.3,
    color: theme.palette.text.secondary,
  },
  // The golfer's request header: the coach panel, then price and order number.
  detailMeta: {
    margin: theme.spacing(1.25, 0, 2.5),
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
    lineHeight: 1.5,
  },
  // In-text link (the order number on the detail page): the same blue as the
  // theme's contained primary button (`palette.blue.main` in the /club theme),
  // no underline except on keyboard focus.
  orderLink: {
    color: theme.palette.blue?.main || theme.palette.primary.main,
    fontWeight: 500,
    '&, &:hover': { textDecoration: 'none' },
    '&:focus-visible': { textDecoration: 'underline' },
  },
  // Status chip in the Lesson Packs chip shape (pill, 22px, 10px/500). "Upload
  // your video" takes the selected left nav item's grey and label color
  // (`palette.sidebar.activeBg` / `itemText`), "Coach reviewing" a light blue
  // with navy text, and "Feedback ready" green. `&&` outranks the shared
  // chip's color class.
  statusChip: {
    '&&': {
      flexShrink: 0,
      height: 22,
      borderRadius: 9999,
      fontSize: '10px',
      fontWeight: 500,
      backgroundColor: '#EBF0F2',
      color: INK,
    },
  },
  statusChipWarning: {
    '&&': {
      backgroundColor: theme.palette.sidebar?.activeBg || '#EBF0F2',
      color: theme.palette.sidebar?.itemText || theme.palette.primary.main,
    },
  },
  statusChipActive: {
    '&&': { backgroundColor: '#E0EAFF', color: theme.palette.primary.main },
  },
  statusChipSuccess: {
    '&&': { backgroundColor: '#DDF5E1', color: '#1B7F37' },
  },
  // Rounded player tile: the radius is clipped here so the native controls
  // and the poster follow it.
  videoTile: {
    overflow: 'hidden',
    borderRadius: 10,
    backgroundColor: INK,
    lineHeight: 0,
  },
  video: {
    display: 'block',
    width: '100%',
    maxHeight: 420,
    borderRadius: 10,
    backgroundColor: INK,
  },
  videoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
    gap: theme.spacing(2),
  },
  // "What you told your coach": label over value rows with hairlines between,
  // like the Order Details rows.
  contextGrid: {
    display: 'flex',
    flexDirection: 'column',
  },
  contextRow: {
    padding: theme.spacing(1.5, 0),
    '&:first-child': { paddingTop: 0 },
    '&:last-child': { paddingBottom: 0 },
    '& + &': { borderTop: `1px solid ${theme.palette.divider}` },
  },
  contextLabel: {
    marginBottom: theme.spacing(0.5),
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.06em',
    lineHeight: 1.3,
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
  },
  responseNotes: { marginTop: theme.spacing(2) },
  responseShared: {
    marginTop: theme.spacing(2),
    fontSize: 13,
    lineHeight: 1.5,
    color: theme.palette.text.secondary,
  },
  notes: {
    whiteSpace: 'pre-wrap',
    fontSize: '0.9375rem',
    lineHeight: 1.6,
    overflowWrap: 'anywhere',
  },
  actionRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(2),
  },
  // Order summary: collapsible on mobile (the header becomes a button)
  orderSummaryHeading: {
    flex: 1,
    minWidth: 0,
    margin: 0,
    fontSize: 'inherit',
    fontWeight: 'inherit',
    lineHeight: 'inherit',
  },
  orderSummaryToggle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    width: '100%',
    minHeight: 44,
    margin: 0,
    padding: 0,
    border: 0,
    borderRadius: 12,
    background: 'none',
    font: 'inherit',
    color: 'inherit',
    textAlign: 'left',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 4,
    },
  },
  orderSummaryToggleCollapsed: { paddingBottom: theme.spacing(2.5) },
  orderSummaryToggleText: { display: 'block', flex: 1, minWidth: 0 },
  orderSummaryCompact: {
    display: 'block',
    marginTop: theme.spacing(1),
    fontSize: '0.9375rem',
    fontWeight: 600,
    lineHeight: 1.4,
    color: theme.palette.text.primary,
  },
  orderSummaryChevron: {
    display: 'inline-flex',
    flexShrink: 0,
    // The glyph has side bearing inside its 28px box; pull it right so the
    // chevron lines up with the card's content inset.
    marginRight: -6,
    color: theme.palette.primary.main,
    '& svg': { fontSize: 28 },
  },
  // List. Native to the /club member pages: the tokens of the Lesson Packs
  // cards and the My Coach coach cards (12px radius, 1px divider border, no
  // shadow, shared hover, sans-serif 16px/600 title, caption metadata, theme
  // contained button). The title link is stretched over the card
  // (`requestLink::after`), so the whole card opens the request while the
  // upload button stays a separate link above it.
  listRoot: {
    margin: 45,
    [theme.breakpoints.down('xs')]: {
      margin: theme.spacing(2),
      marginBottom: 70,
    },
  },
  listToolbar: {
    display: 'flex',
    alignItems: 'center',
    minHeight: 40,
    marginBottom: theme.spacing(2),
  },
  listTitle: {
    margin: 0,
    fontSize: '1.125rem',
    fontWeight: 700,
    lineHeight: 1.3,
  },
  requestCard: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    boxSizing: 'border-box',
    padding: theme.spacing(2.5, 2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    backgroundColor: theme.palette.common.white,
    ...interactiveCardHoverStyles(theme),
  },
  requestBadgeRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: theme.spacing(2),
  },
  requestTitle: {
    margin: 0,
    marginBottom: theme.spacing(1.5),
    fontSize: '1rem',
    fontWeight: 600,
    lineHeight: 1.3,
    color: theme.palette.text.primary,
  },
  requestLink: {
    color: 'inherit',
    // No underline in any state: the card's own hover border and shadow
    // (shared with the Lesson Packs and My Coach cards) are the feedback.
    '&, &:hover, &:focus, &:active, &:visited': {
      color: 'inherit',
      textDecoration: 'none',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      borderRadius: 12,
    },
    '&:focus': { outline: 'none' },
    // Keyboard focus ring around the whole card (the stretched `::after`).
    // Drawn inside the edge: MUI's `Card` clips overflow, so an outset ring
    // would be cut off.
    '&:focus-visible::after': {
      outline: '2px solid #0053FF',
      outlineOffset: -2,
    },
  },
  listGrid: { listStyle: 'none', padding: 0 },
  listItem: { display: 'flex' },
  requestMeta: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
  requestCoach: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    minWidth: 0,
  },
  // Same avatar colors as the My Coach coach cards.
  requestCoachAvatar: {
    width: 24,
    height: 24,
    fontSize: '0.6875rem',
    fontWeight: 500,
    color: '#00205B',
    backgroundColor: '#E8F1FF',
    textTransform: 'uppercase',
  },
  requestText: {
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
    lineHeight: 1.3,
  },
  requestFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 'auto',
    paddingTop: theme.spacing(2),
    [theme.breakpoints.down('xs')]: { '& > *': { flex: 1 } },
  },
  requestAction: {
    position: 'relative',
    zIndex: 1,
  },
  listEmpty: { padding: theme.spacing(4, 2) },
}))

export default useStyles
